import { useState } from 'react'
/**
 * @typedef {Object} ChrofyFormSelectProps
 * @property {string} value
 * @property {string} className
 * @property {function( any ):void} onChange
 * @property {{ label: string; value: any; extra: any }[]} options
 * @property {boolean} disabled
 * @property {boolean} hasNoSelect
 * @property {string} selectClassName
 * @property {boolean} lightNoSelect
 * @property {string} data-testid
 */

/**
 * @function ChrofyFormSelect
 * @type {React.FC<ChrofyFormSelectProps>}
 * @component
 */
export const ChrofyFormSelect = (props) => {
  const {
    id,
    value,
    onChange,
    className,
    options: _options = [],
    disabled,
    hasNoSelect = true,
    selectClassName = '',
    lightNoSelect = true
  } = props

  const options = hasNoSelect ? [{ label: '未選択', value: '' }, ..._options] : _options

  const [openSelect, setOpenSelect] = useState(false)

  const textLight = lightNoSelect && value === '' ? ' text-gray-700/50 ' : ''
  const selectStyles = selectClassName + ' ' + textLight

  const selectedLabel = options.find((e) => e.value === value)?.label ?? ''

  return (
    <div className={`form-control  ${className}`}>
      <div className="select-control">
        <select
          id={id}
          disabled={disabled}
          className={`form-select select-p-large rounded-lg text-h7 pr-10 w-[100%] ${selectStyles} `}
          value={value}
          onClick={() => setOpenSelect(true)}
          onBlur={() => setOpenSelect(false)}
          onChange={onChange}
          title={selectedLabel || ''}
          data-testid={props['data-testid']}
        >
          {options.map((e, index) => {
            return (
              <option value={e.value} key={index} className="text-gray-900">
                {e.label}
              </option>
            )
          })}
        </select>
        <div className="select-control__icon flex text-base items-center justify-center w-8 absolute pointer-events-none inset-y-0 right-0">
          <span className={`icon-arrow ${openSelect ? '-rotate-90' : 'rotate-90'}`}></span>
        </div>
      </div>
    </div>
  )
}

/** @function ChrofyTableFormInput */
export const ChrofyTableFormInput = (props) => {
  const { id, label, required = true, placeholder, value, errors, disabled, onChange, isError, title } = props
  return (
    <tr>
      <th className="form-table__col-head w-[150px]">
        <label htmlFor={id} className="text-h7 font-bold">
          {label} {required && <span className="red--text">*</span>}
        </label>
      </th>
      <td className="form-table__col-data">
        <input
          data-testid={props?.['data-testid']}
          id={id}
          name={id}
          className={`text-input w-full text-left ${isError && 'is-error'}`}
          type="text"
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          title={title}
        />
        <ChrofyShowErrors field={id} errors={errors} />
      </td>
    </tr>
  )
}

export const ChrofyShowErrors = ({ errors: errorMessage, field = '' }) => {
  if (errorMessage?.[field] == null) return <></>

  const list = Array.isArray(errorMessage[field]) ? errorMessage[field] : [errorMessage[field]]
  return (
    <>
      {list.map((err, index) => (
        <p key={index} className="text-red-500 text-sm">
          {err}
        </p>
      ))}
    </>
  )
}

export const RequiredField = (props) => <span className={`red--text ${props?.className ?? ''}`}>*</span>

/**
 * @function convertSchemaErrors
 * @param {import('zod/mini').ZodMiniObject} schema
 * @param {object} values
 */
export const convertSchemaErrors = (schema, values) => {
  const parsed = schema.safeParse(values)

  const issues = parsed.error?.issues ?? []
  const errorObj = issues.reduce((acc, c) => {
    const path = c.path[0]
    if (!acc[path]) {
      acc[path] = []
    }
    acc[path].push(c.message)
    return acc
  }, {})

  return errorObj
}
